<template>
  <div>
    <!-- Page Title -->
    <h1 class="text-2xl font-medium text-gray-800 mb-8 p-6">Frequent Beneficiary</h1>

    <!-- Internal Beneficiaries Section -->
    <div class="mb-8">
      <h2 class="text-lg font-medium text-gray-700 mb-4">INTERNAL BENEFICIARIES</h2>
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div class="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 bg-gray-50">
          <div class="col-span-4 text-sm font-medium text-gray-600">Name</div>
          <div class="col-span-5 text-sm font-medium text-gray-600">Beneficiary Account</div>
          <div class="col-span-3 text-sm font-medium text-gray-600 text-right">Operations</div>
        </div>
        <div v-for="beneficiary in beneficiariesFiltered('internal')" :key="beneficiary.id" class="grid grid-cols-12 gap-4 p-4 border-b border-gray-200">
          <div class="col-span-4 text-sm text-gray-500">{{ beneficiary.name }}</div>
          <div class="col-span-5 text-sm text-gray-500">{{ beneficiary.account_number }}</div>
          <div class="col-span-3 flex justify-end gap-2">
            <button @click="editBeneficiary(beneficiary)" class="p-1 text-primary hover:text-orange-600">
              <component :is="UserRoundPen" class="w-6 h-6"/>
            </button>
            <button @click="deleteBeneficiary(beneficiary.id)" class="p-1 text-primary hover:text-orange-600">
              <component :is="Trash2" class="w-6 h-6"/>
            </button>
          </div>
        </div>
      </div>
      <div class="flex justify-center mt-4">
        <button @click="openModal('internal')" class="bg-primary text-white px-8 py-2 rounded-lg font-medium">
          NEW BENEFICIARY
        </button>
      </div>
    </div>

    <!-- Other National and European Bank Beneficiaries Section -->
    <div class="mb-8">
      <h2 class="text-lg font-medium text-gray-700 mb-4">OTHER NATIONAL AND EUROPEAN BANK BENEFICIARIES</h2>
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div class="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 bg-gray-50">
          <div class="col-span-4 text-sm font-medium text-gray-600">Description</div>
          <div class="col-span-5 text-sm font-medium text-gray-600">Beneficiary Account</div>
          <div class="col-span-3 text-sm font-medium text-gray-600 text-right">Operations</div>
        </div>
        <div v-for="beneficiary in beneficiariesFiltered('national','european')" :key="beneficiary.id" class="grid grid-cols-12 gap-4 p-4 border-b border-gray-200">
          <div class="col-span-4 text-sm text-gray-500">{{ beneficiary.name }}</div>
          <div class="col-span-5 text-sm text-gray-500">{{ beneficiary.iban }}</div>
          <div class="col-span-3 flex justify-end gap-2">
            <button @click="editBeneficiary(beneficiary)" class="p-1 text-primary hover:text-orange-600">
              <component :is="UserRoundPen" class="w-6 h-6"/>
            </button>
            <button @click="deleteBeneficiary(beneficiary.id)" class="p-1 text-primary hover:text-orange-600">
              <component :is="Trash2" class="w-6 h-6"/>
            </button>
          </div>
        </div>
      </div>
      <div class="flex justify-center mt-4">
        <button @click="openModal('european')" class="bg-primary text-white px-8 py-2 rounded-lg font-medium">
          NEW BENEFICIARY
        </button>
      </div>
    </div>

    <!-- Other International Bank Beneficiaries Section -->
    <div class="mb-8">
      <h2 class="text-lg font-medium text-gray-700 mb-4">OTHER INTERNATIONAL BANK BENEFICIARIES</h2>
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div class="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 bg-gray-50">
          <div class="col-span-4 text-sm font-medium text-gray-600">Description</div>
          <div class="col-span-5 text-sm font-medium text-gray-600">Beneficiary Account</div>
          <div class="col-span-3 text-sm font-medium text-gray-600 text-right">Operations</div>
        </div>
        <div v-for="beneficiary in beneficiariesFiltered('international')" :key="beneficiary.id" class="grid grid-cols-12 gap-4 p-4 border-b border-gray-200">
          <div class="col-span-4 text-sm text-gray-500">{{ beneficiary.name }}</div>
          <div class="col-span-5 text-sm text-gray-500">{{ beneficiary.account_number }}</div>
          <div class="col-span-3 flex justify-end gap-2">
            <button @click="editBeneficiary(beneficiary)" class="p-1 text-primary hover:text-orange-600">
              <component :is="UserRoundPen" class="w-6 h-6"/>
            </button>
            <button @click="deleteBeneficiary(beneficiary.id)" class="p-1 text-primary hover:text-orange-600">
              <component :is="Trash2" class="w-6 h-6"/>
            </button>
          </div>
        </div>
      </div>
      <div class="flex justify-center mt-4">
        <button @click="openModal('international')" class="bg-primary text-white px-8 py-2 rounded-lg font-medium">
          NEW BENEFICIARY
        </button>
      </div>
    </div>


    <NewBeneficiaryModal
        :isOpen="isModalOpen"
        :type="modalType"
        :beneficiary="editingBeneficiary"
        @close="isModalOpen = false"
        @submitted="onBeneficiaryCreated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Trash2, UserRoundPen } from 'lucide-vue-next'
import api from '@/services/api'
import NewBeneficiaryModal from "@/components/transfer/NewBeneficiaryModal.vue";

const beneficiaries = ref([])

// Modal state
// const modalVisible = ref(false)
// const modalDescription = ref('')
// const modalAccount = ref('')
const editingBeneficiary = ref(null)

const isModalOpen = ref(false)
const modalType = ref('european')



// Fetch beneficiaries
const fetchBeneficiaries = async () => {
  try {
    const res = await api.get('/beneficiaries', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
    beneficiaries.value = res.data.data
  } catch (err) {
    console.error('Error fetching beneficiaries:', err.response?.data || err)
  }
}

// Filter beneficiaries by type
const beneficiariesFiltered = (...types) => {
  return beneficiaries.value.filter(b => types.includes(b.type))
}

const openModal = (type) => {
  modalType.value = type
  isModalOpen.value = true
}

const onBeneficiaryCreated = (newBene) => {
  const updated = newBene.data || newBene  // بسته به ریسپانس API
  if (editingBeneficiary.value) {
    // update in list
    const index = beneficiaries.value.findIndex(b => b.id === updated.id)
    if (index !== -1) beneficiaries.value[index] = updated
  } else {
    // create
    beneficiaries.value.push(updated)
  }

  editingBeneficiary.value = null
  isModalOpen.value = false
}


// Open modal to add new beneficiary
// const newBeneficiary = (type) => {
//   modalType.value = type
//   modalDescription.value = ''
//   modalAccount.value = ''
//   editingBeneficiary.value = null
//   modalVisible.value = true
// }

// Open modal to edit beneficiary
const editBeneficiary = (beneficiary) => {
  modalType.value = beneficiary.type
  editingBeneficiary.value = beneficiary
  isModalOpen.value = true
}


// Save beneficiary (create or update)
// const saveBeneficiary = async () => {
//   try {
//     if (editingBeneficiary.value) {
//       // Update
//       const res = await api.put(`/beneficiaries/${editingBeneficiary.value.id}`, {
//         description: modalDescription.value,
//         account_number: modalAccount.value
//       }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
//       const index = beneficiaries.value.findIndex(b => b.id === editingBeneficiary.value.id)
//       beneficiaries.value[index] = res.data.data
//     } else {
//       // Create
//       const res = await api.post('/beneficiaries', {
//         description: modalDescription.value,
//         account_number: modalAccount.value,
//         type: modalType.value
//       }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
//       beneficiaries.value.push(res.data.data)
//     }
//     modalVisible.value = false
//   } catch (err) {
//     console.error('Error saving beneficiary:', err.response?.data || err)
//     alert('Error saving beneficiary')
//   }
// }

// Delete beneficiary
const deleteBeneficiary = async (id) => {
  if (!confirm('Are you sure you want to delete this beneficiary?')) return
  try {
    await api.delete(`/beneficiaries/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
    beneficiaries.value = beneficiaries.value.filter(b => b.id !== id)
  } catch (err) {
    console.error('Error deleting beneficiary:', err.response?.data || err)
    alert('Error deleting beneficiary')
  }
}

onMounted(fetchBeneficiaries)
</script>
